import { useState } from 'react';
import { Search, Calendar, Clock, ArrowRight, TrendingUp, BookOpen, Code, Lightbulb, Rocket } from 'lucide-react';
import { Link } from 'react-router-dom';
import { articulos } from './blog/articulos-data.jsx';
import SEOHead from '../components/SEOHead';

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'Todos', icon: <BookOpen size={18} /> },
    { id: 'desarrollo', name: 'Desarrollo', icon: <Code size={18} /> },
    { id: 'diseño', name: 'Diseño', icon: <Lightbulb size={18} /> },
    { id: 'marketing', name: 'Marketing', icon: <TrendingUp size={18} /> },
    { id: 'startups', name: 'Startups', icon: <Rocket size={18} /> }
  ];

  const filteredArticles = articulos.filter(article => {
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         article.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredArticle = articulos[0];

  // Structured data para el blog
  const blogStructuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Blog VaporDesigns",
    "description": "Blog especializado en desarrollo web, diseño UX/UI y marketing digital. Tutoriales, guías y tendencias actuales en tecnología.",
    "url": "https://vapordesigns.netlify.app/blog",
    "publisher": {
      "@type": "Organization",
      "name": "VaporDesigns",
      "logo": {
        "@type": "ImageObject",
        "url": "https://vapordesigns.netlify.app/images/LogoDefinitivo.png"
      }
    },
    "blogPost": articulos.map(articulo => {
      // Convertir fecha española a ISO (formato: "2 Dic 2024")
      const meses = {
        'Ene': '01', 'Feb': '02', 'Mar': '03', 'Abr': '04',
        'May': '05', 'Jun': '06', 'Jul': '07', 'Ago': '08',
        'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dic': '12'
      };
      
      let datePublished = '2024-12-01T00:00:00Z'; // Fecha por defecto
      try {
        const [dia, mes, año] = articulo.date.split(' ');
        const mesNumero = meses[mes] || '01';
        const diaFormateado = dia.padStart(2, '0');
        datePublished = `${año}-${mesNumero}-${diaFormateado}T00:00:00Z`;
      } catch (e) {
        console.warn('Error parseando fecha:', articulo.date);
      }
      
      return {
        "@type": "BlogPosting",
        "headline": articulo.title,
        "description": articulo.excerpt,
        "url": `https://vapordesigns.netlify.app/blog/${articulo.slug}`,
        "datePublished": datePublished,
        "author": {
          "@type": "Organization",
          "name": articulo.author
        },
        "image": "https://vapordesigns.netlify.app/images/LogoDefinitivo.png",
        "keywords": articulo.tags.join(', '),
        "wordCount": parseInt(articulo.readTime) * 200
      };
    })
  };

  return (
    <>
      <SEOHead 
        title="Blog de Desarrollo Web y Diseño Digital - VaporDesigns"
        description="Blog especializado en desarrollo web, diseño UX/UI y marketing digital. Tutoriales, guías y tendencias actuales para hacer crecer tu negocio online. ¡Descubre contenido de valor!"
        keywords="blog desarrollo web, tutoriales diseño web, marketing digital, UX/UI, React, WordPress, SEO, e-commerce, programación"
        type="website"
        url="/blog"
        structuredData={blogStructuredData}
      />
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950/30 to-slate-950 pt-24 pb-16 relative overflow-hidden">
      {/* Efectos de fondo */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-20 left-10 w-96 h-96 bg-indigo-500/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-emerald-500/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Puntos flotantes */}
      <div className="absolute top-32 left-20 w-2 h-2 bg-indigo-400 rounded-full animate-pulse"></div>
      <div className="absolute top-64 right-32 w-2 h-2 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
      <div className="absolute bottom-32 left-1/3 w-2 h-2 bg-emerald-400 rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>

      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(99,102,241,0.1),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(168,85,247,0.1),transparent_50%)]"></div>

      <div className="relative z-10 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Blog <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">Vapor Designs</span>
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Tutoriales, guías y las últimas tendencias en desarrollo web, diseño y marketing digital
            </p>
          </div>

          {/* Search & Filters */}
          <div className="mb-12">
            <div className="max-w-2xl mx-auto mb-8">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                <input
                  type="text"
                  placeholder="Buscar artículos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-slate-800/50 border border-slate-700 rounded-2xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all backdrop-blur-sm"
                />
              </div>
            </div>

            {/* Categories */}
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all flex items-center gap-2 ${
                    selectedCategory === cat.id
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-indigo-500/30'
                      : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50 border border-slate-700'
                  }`}
                >
                  {cat.icon}
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {/* Featured Article */}
          {selectedCategory === 'all' && !searchTerm && (
            <div className="mb-16">
              <div className="relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-3xl overflow-hidden border border-slate-700/50 shadow-2xl group hover:shadow-indigo-500/20 transition-all duration-500">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="grid md:grid-cols-2 gap-8 p-8 relative z-10">
                  <div className="aspect-video rounded-2xl overflow-hidden">
                    <div className="w-full h-full bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 flex items-center justify-center">
                      <div className="text-6xl">💻</div>
                    </div>
                  </div>
                  <div className="flex flex-col justify-center">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="px-3 py-1 bg-indigo-500/20 text-indigo-300 rounded-full text-sm font-semibold border border-indigo-500/30">
                        ⭐ Destacado
                      </span>
                      <span className="px-3 py-1 bg-slate-700/50 text-slate-300 rounded-full text-sm">
                        {featuredArticle.category}
                      </span>
                    </div>
                    <h2 className="text-3xl font-bold text-white mb-4 group-hover:text-indigo-400 transition-colors">
                      {featuredArticle.title}
                    </h2>
                    <p className="text-slate-300 mb-6">
                      {featuredArticle.excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-slate-400 mb-6">
                      <div className="flex items-center gap-1">
                        <Calendar size={16} />
                        {featuredArticle.date}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock size={16} />
                        {featuredArticle.readTime}
                      </div>
                    </div>
                    <Link
                      to={`/blog/${featuredArticle.slug}`}
                      className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg shadow-indigo-500/30 w-fit group"
                    >
                      Leer Artículo
                      <ArrowRight className="group-hover:translate-x-1 transition-transform" size={18} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Articles Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArticles.slice(selectedCategory === 'all' && !searchTerm ? 1 : 0).map((article) => (
              <Link
                key={article.id}
                to={`/blog/${article.slug}`}
                className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl overflow-hidden border border-slate-700/50 shadow-xl hover:shadow-indigo-500/20 transition-all duration-500 group hover:scale-105"
              >
                <div className="aspect-video flex items-center justify-center overflow-hidden group-hover:scale-110 transition-transform duration-500">
                  <div className={`w-full h-full flex items-center justify-center ${
                    article.category === 'desarrollo' ? 'bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700' :
                    article.category === 'diseño' ? 'bg-gradient-to-br from-purple-600 via-pink-600 to-rose-700' :
                    article.category === 'marketing' ? 'bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700' :
                    'bg-gradient-to-br from-indigo-900/50 to-purple-900/50'
                  }`}>
                    <div className="text-5xl">
                      {article.category === 'desarrollo' ? '💻' :
                       article.category === 'diseño' ? '🎨' :
                       article.category === 'marketing' ? '📈' : '📝'}
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-3 py-1 bg-indigo-500/20 text-indigo-300 rounded-full text-xs font-semibold border border-indigo-500/30">
                      {article.category}
                    </span>
                    <div className="flex items-center gap-1 text-xs text-slate-400">
                      <Clock size={14} />
                      {article.readTime}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-indigo-400 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-slate-300 text-sm mb-4 line-clamp-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-xs text-slate-400">
                      <Calendar size={14} />
                      {article.date}
                    </div>
                    <span className="flex items-center gap-1 text-indigo-400 hover:text-indigo-300 text-sm font-semibold">
                      Leer más
                      <ArrowRight className="group-hover:translate-x-1 transition-transform" size={16} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* No Results */}
          {filteredArticles.length === 0 && (
            <div className="text-center py-16">
              <BookOpen className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">No se encontraron artículos</h3>
              <p className="text-slate-400">Intenta con otros términos de búsqueda o categorías</p>
            </div>
          )}

          {/* Newsletter CTA */}
          <div className="mt-16 bg-gradient-to-br from-indigo-900/40 to-purple-900/40 backdrop-blur-xl rounded-3xl border border-indigo-500/30 p-12 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Suscríbete al Newsletter
            </h2>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              Recibe los últimos artículos, tutoriales y recursos directamente en tu correo. Sin spam, solo contenido de valor.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
              <input
                type="email"
                placeholder="tu@email.com"
                className="flex-1 px-6 py-4 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              />
              <button className="px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg shadow-indigo-500/30 whitespace-nowrap">
                Suscribirse
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Blog;
